import React, { useState } from 'react';
import { customersApi } from '../../../services/api';
import { Customer } from '../../../types';
import { showSuccess } from '../../../utils/toast';

interface UseCustomerPanelDeps {
  loadContacts: (customerId: number) => Promise<void>;
  resetContactState: () => void;
  load: () => Promise<void>;
  setError: (msg: string | null) => void;
}

export const useCustomerPanel = ({
  loadContacts,
  resetContactState,
  load,
  setError,
}: UseCustomerPanelDeps) => {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [viewMode, setViewMode] = useState<'view' | 'edit' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const withDefaultDriveUrl = (customer: Customer): Customer => ({
    ...customer,
    google_drive_url: customer.google_drive_url || 'https://drive.google.com/',
  });

  const handleViewDetails = (customer: Customer) => {
    setSelectedCustomer(withDefaultDriveUrl(customer));
    setViewMode('view');
    loadContacts(customer.id);
  };

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(withDefaultDriveUrl(customer));
    setViewMode('edit');
  };

  const handleClosePanel = () => {
    setSelectedCustomer(null);
    setViewMode(null);
    resetContactState();
  };

  const handleCustomerChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!selectedCustomer) return;
    const { name, value } = e.target;
    setSelectedCustomer((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  const handleUpdateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomer || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await customersApi.update(selectedCustomer.id, selectedCustomer);
      await load();
      handleClosePanel();
      showSuccess('Customer updated successfully');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return {
    selectedCustomer,
    setSelectedCustomer,
    viewMode,
    setViewMode,
    submitting,
    handleViewDetails,
    handleEdit,
    handleClosePanel,
    handleCustomerChange,
    handleUpdateCustomer,
  };
};
